/** @jsxImportSource react */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { readCookie, writeCookie } from '@zapier/cookies';
import { getAccountIdFromCookie } from '../utils/getAccountIdFromCookie';
import { fetchSession } from '../client/fetchSession';
import { getCurrentAccountCookieDomain } from '../utils/getCurrentAccountCookieDomain';
import { getSsoHintFromCookie } from '../utils/getSsoHintFromCookie';
import {
  ACCOUNT_COOKIE_NAME,
  ACCOUNT_RECONCILE_TTL,
  ACCOUNT_RECONCILED_AT_STORAGE_NAME,
} from '../constants';

// localStorage is deliberately best-effort here: when it is unavailable
// (privacy modes, disabled storage), the throttle fails open and we reconcile —
// correctness over traffic savings for that minority of sessions.
function reconcileTtlElapsed(): boolean {
  try {
    const reconciledAt = Number(
      window.localStorage.getItem(ACCOUNT_RECONCILED_AT_STORAGE_NAME)
    );
    const age = Date.now() - reconciledAt;
    // A negative age means the stored timestamp is in the future (clock skew,
    // or a clock corrected backwards). Treat it as elapsed — the cost is one
    // extra session call, whereas honoring it would wedge the throttle shut
    // and suppress reconciliation until real time catches up.
    return !Number.isFinite(age) || age < 0 || age >= ACCOUNT_RECONCILE_TTL;
  } catch {
    return true;
  }
}

// Recorded BEFORE the session fetch (not after it succeeds) so concurrent
// mounts — multiple provider instances, React StrictMode's dev double-mount —
// collapse into a single request. A failed fetch consumes the window and
// retries on the first mount after the TTL.
function markReconcileAttempt(): void {
  try {
    window.localStorage.setItem(
      ACCOUNT_RECONCILED_AT_STORAGE_NAME,
      String(Date.now())
    );
  } catch {
    // Storage unavailable — nothing to record.
  }
}

// Context for identity-related values
const IdentityContext = createContext<{
  currentAccountId: number | undefined;
  ssoHint: string | undefined;
}>({
  currentAccountId: undefined,
  ssoHint: undefined,
});

type Props = {
  children: ReactNode;
  currentAccountId?: number;
};

// Hooks to consume identity values
export const useIdentity = () => useContext(IdentityContext);

export function IdentityProvider({
  currentAccountId: currentAccountIdProp,
  ...props
}: Props) {
  const [currentAccountId, setCurrentAccountId] = useState<number | undefined>(
    currentAccountIdProp ?? getAccountIdFromCookie()
  );

  const [ssoHint, setSsoHint] = useState<string | undefined>(
    getSsoHintFromCookie()
  );

  /** Watch cookies for changes */
  async function watchCookies() {
    const accountIdFromCookie = getAccountIdFromCookie();
    const ssoHintFromCookie = getSsoHintFromCookie();

    // Only take action if this is a real change to account ID
    if (accountIdFromCookie && accountIdFromCookie !== currentAccountId) {
      // Account id has changed
      setCurrentAccountId(accountIdFromCookie);
    }

    // Only take action if this is a real change to SSO hint
    if (ssoHintFromCookie !== ssoHint) {
      setSsoHint(ssoHintFromCookie);
    }
  }

  const checkSession = useCallback(async () => {
    try {
      const session = await fetchSession();
      if (!session?.current_account_id) {
        return;
      }
      const sessionAccountId = session.current_account_id;

      // Skip the write when the cookie already agrees with the session, so a
      // healthy cookie (and whatever attributes the backend set on it) is left
      // untouched. The write only matters when the cookie is absent or stale.
      const cookieAccountId = readCookie(ACCOUNT_COOKIE_NAME);
      if (String(cookieAccountId) === String(sessionAccountId)) {
        return;
      }

      // Sync the currentAccountId cookie with the same registrable domain (.zapier.com
      // in prod) that the backend's AccountMiddleware uses.  Writing with a matching
      // domain means we OVERWRITE the backend's shared cookie rather than minting a
      // second, host-only sibling — the host-only sibling is what triggers the TA-44
      // conflict-detection branch in getAccountIdFromCookie.
      const cookieDomain = getCurrentAccountCookieDomain();
      writeCookie('currentAccountId', sessionAccountId, {
        path: '/',
        sameSite: 'strict',
        category: 'essential',
        ...(cookieDomain !== undefined ? { domain: cookieDomain } : {}),
      });
    } catch (error) {
      console.error('Failed to fetch session:', error);
    }
  }, []);

  useEffect(() => {
    // This supports use cases like Admin Center, where the subdomain issues a JWT
    // scoped to a specific account. The account ID from that JWT is passed directly
    // as a prop rather than being read from a cookie. For most use cases, this prop
    // is not needed and the account ID will be derived from the cookie automatically.
    // When provided, cookie polling is skipped so the JWT-authenticated account
    // cannot be overridden by the shared-domain cookie.
    if (currentAccountIdProp !== undefined) {
      setCurrentAccountId(currentAccountIdProp);
      return;
    }

    // Read cookie values on a set interval to watch for changes
    const timer = setInterval(watchCookies, 1000);

    /** Remove cookie watcher when we dismount */
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [currentAccountIdProp, currentAccountId, ssoHint]);

  useEffect(() => {
    // JWT-scoped account passed as a prop (e.g. Admin Center) — never let the
    // shared-domain cookie or the session override it.
    if (currentAccountIdProp !== undefined) {
      return;
    }

    // Reconcile the cookie against the authoritative session even when the
    // cookie is PRESENT — previously this only ran when it was absent, so a
    // stale cookie left behind by an account switch was trusted forever and
    // split account context between session-scoped and cookie-scoped calls
    // (IDPLAT-3016). checkSession overwrites a divergent cookie with the
    // session's account; watchCookies then picks the corrected value up into
    // state within a tick.
    //
    // A present cookie is re-validated at most once per ACCOUNT_RECONCILE_TTL
    // per browser, so the reconciliation adds at most a handful of
    // /api/v4/session/ calls per user per hour instead of one per page load.
    // An absent cookie still reconciles immediately (bootstrap — pre-existing
    // behavior), at the cost of a stale cookie healing up to one TTL later.
    const hasAccountCookie = Boolean(readCookie(ACCOUNT_COOKIE_NAME));
    if (hasAccountCookie && !reconcileTtlElapsed()) {
      return;
    }
    markReconcileAttempt();
    void checkSession();
  }, [currentAccountIdProp, checkSession]);

  // Combine identity values into a single context value
  const identityValue = useMemo(
    () => ({
      currentAccountId,
      ssoHint,
    }),
    [currentAccountId, ssoHint]
  );

  /** Create provider */
  return (
    <IdentityContext.Provider value={identityValue}>
      {props.children}
    </IdentityContext.Provider>
  );
}
